'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || typeof window === 'undefined') return;

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: url,
        page_title: document.title,
      });
    }
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// ── Moderation Event Tracking ─────────────────────────────────────────────────
// Action types for curator / admin / historian moderation actions
export type ModerationAction =
  | 'approve' |'reject' |'bulk_approve' |'bulk_reject' |'flag_reviewed' |'flag_dismissed' |'corroborate' |'dispute' |'needs_more_info';

export interface ModerationEventPayload {
  action: ModerationAction;
  actorId: string;
  actorRole: string;
  contributionId?: string;
  reportId?: string;
  metadata?: Record<string, any>;
}

/**
 * Tracks a moderation action to both Google Analytics and the
 * Supabase `moderation_events` table for reviewer behaviour analytics,
 * queue velocity measurement, and platform moderation pattern reporting.
 */
export async function trackModerationEvent(payload: ModerationEventPayload): Promise<void> {
  const { action, actorId, actorRole, contributionId, reportId, metadata = {} } = payload;

  // 1. Fire GA event (non-blocking, client-side only)
  trackEvent('moderation_action', {
    moderation_action: action,
    actor_role: actorRole,
    contribution_id: contributionId ?? null,
    report_id: reportId ?? null,
    ...metadata,
  });

  // 2. Persist to Supabase (async, best-effort — errors are swallowed to avoid
  //    blocking the primary curator/historian workflow)
  try {
    // Dynamic import keeps the Supabase client out of the initial bundle
    const { createClient } = await import('@/lib/supabase/client');
    const supabase = createClient();
    await supabase.from('moderation_events').insert({
      actor_id: actorId,
      actor_role: actorRole,
      action,
      contribution_id: contributionId ?? null,
      report_id: reportId ?? null,
      metadata,
    });
  } catch (err: any) {
    // Non-fatal — analytics should never break the primary workflow
    console.warn('[trackModerationEvent] Supabase insert failed:', err?.message);
  }
}
